import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { axiosInstance } from "../App";
import { ArrowLeft, BarChart3, Users, Clock, Download, TrendingUp, DollarSign, Activity } from "lucide-react";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import CostBurnReport from '../components/CostBurnReport';
import UtilizationReport from '../components/UtilizationReport';
import ResourceSelector from '../components/ResourceSelector';

function Reports() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [performanceData, setPerformanceData] = useState([]);
  const [timeData, setTimeData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState("all");
  const [days, setDays] = useState(30);

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (projects.length > 0 || selectedProject === "all") {
      fetchAllData();
    }
  }, [selectedProject, days]);

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get("/projects");
      setProjects(response.data);
      
      // İlk projeyi otomatik seç
      if (response.data.length > 0 && selectedProject === "all") {
        setSelectedProject(response.data[0].id);
      }
    } catch (error) {
      console.error("Failed to fetch projects", error);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchChartData(),
        fetchPerformanceData(),
        fetchTimeData()
      ]);
    } catch (error) {
      toast.error(t('dataLoadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== "all") params.append("project_id", selectedProject);
      params.append("days", days);

      const response = await axiosInstance.get(`/reports/charts?${params.toString()}`);
      setChartData(response.data);
    } catch (error) {
      console.error("Failed to fetch chart data", error);
    }
  };

  const fetchPerformanceData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== "all") params.append("project_id", selectedProject);

      const response = await axiosInstance.get(`/reports/performance?${params.toString()}`);
      setPerformanceData(response.data.performance);
    } catch (error) {
      console.error("Failed to fetch performance data", error);
    }
  };

  const fetchTimeData = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== "all") params.append("project_id", selectedProject);

      const response = await axiosInstance.get(`/reports/time?${params.toString()}`);
      setTimeData(response.data);
    } catch (error) {
      console.error("Failed to fetch time data", error);
    }
  };

  const handleExport = async (exportType) => {
    try {
      const params = new URLSearchParams();
      if (selectedProject !== "all") params.append("project_id", selectedProject);
      params.append("export_type", exportType);

      const response = await axiosInstance.get(`/reports/export?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `rapor_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success(t('exportSuccess'));
    } catch (error) {
      toast.error(t('exportFailed'));
    }
  };

  const handleExportCost = async () => {
    if (!selectedProject || selectedProject === "all") {
      toast.error(t('projectRequired'));
      return;
    }

    try {
      const response = await axiosInstance.get(`/export/project/${selectedProject}/cost.xlsx`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `maliyet_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Maliyet raporu indirildi");
    } catch (error) {
      toast.error("Export başarısız");
    }
  };

  const handleExportAllocations = async () => {
    if (!selectedProject || selectedProject === "all") {
      toast.error(t('projectRequired'));
      return;
    }

    try {
      const response = await axiosInstance.get(`/export/project/${selectedProject}/allocations.xlsx`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `atamalar_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Kaynak atamaları raporu indirildi");
    } catch (error) {
      toast.error("Export başarısız");
    }
  };

  const formatTime = (minutes) => {
    if (minutes < 60) return `${minutes}dk`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}s ${mins}dk`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center py-12">{t('loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate("/dashboard")}
            className="mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('backToDashboard')}
          </Button>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-4">
              <img 
                src="/milestora-logo-light.png" 
                alt="Milestora" 
                className="h-12 w-auto object-contain"
              />
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <BarChart3 className="h-7 w-7" />
                  {t('reports')}
                </h1>
                <p className="text-gray-600 mt-1">
                  {t('projectProgress')}
                </p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={t('selectProjectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Projeler</SelectItem>
                  {projects.map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={days.toString()} onValueChange={(val) => setDays(parseInt(val))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 Gün</SelectItem>
                  <SelectItem value="30">30 Gün</SelectItem>
                  <SelectItem value="90">90 Gün</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="charts" className="w-full">
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="charts">{t('charts')}</TabsTrigger>
            <TabsTrigger value="costburn">{t('costBurn')}</TabsTrigger>
            <TabsTrigger value="utilization">{t('utilization')}</TabsTrigger>
            <TabsTrigger value="performance">{t('performance')}</TabsTrigger>
            <TabsTrigger value="time">{t('timeReport')}</TabsTrigger>
            <TabsTrigger value="export">{t('export')}</TabsTrigger>
          </TabsList>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-6">
            {/* Timeline Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Görev Zaman Çizelgesi</CardTitle>
                <CardDescription>Günlük görev oluşturma ve tamamlama</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData?.timeline || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(date) => new Date(date).toLocaleDateString('tr-TR')}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="created" stroke="#3B82F6" name="Oluşturulan" />
                    <Line type="monotone" dataKey="completed" stroke="#10B981" name="Tamamlanan" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Status Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Durum Dağılımı</CardTitle>
                  <CardDescription>Görevlerin mevcut durumu</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData?.status_distribution || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {(chartData?.status_distribution || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Priority Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle>Öncelik Dağılımı</CardTitle>
                  <CardDescription>Görevlerin öncelik seviyeleri</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={chartData?.priority_distribution || []}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {(chartData?.priority_distribution || []).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Burndown Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Burndown Chart</CardTitle>
                <CardDescription>Kalan görevler vs ideal ilerleme</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={chartData?.burndown || []}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => new Date(date).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' })}
                    />
                    <YAxis />
                    <Tooltip 
                      labelFormatter={(date) => new Date(date).toLocaleDateString('tr-TR')}
                    />
                    <Legend />
                    <Line type="monotone" dataKey="remaining" stroke="#EF4444" name="Kalan Görevler" strokeWidth={2} />
                    <Line type="monotone" dataKey="ideal" stroke="#9CA3AF" name="İdeal İlerleme" strokeDasharray="5 5" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Üye Performans Raporu
                </CardTitle>
                <CardDescription>Ekip üyelerinin detaylı performans metrikleri</CardDescription>
              </CardHeader>
              <CardContent>
                {performanceData.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Veri bulunamadı</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-3 font-semibold">Üye</th>
                          <th className="text-center p-3 font-semibold">Atanan</th>
                          <th className="text-center p-3 font-semibold">Tamamlanan</th>
                          <th className="text-center p-3 font-semibold">Tamamlanma %</th>
                          <th className="text-center p-3 font-semibold">Toplam Süre</th>
                          <th className="text-center p-3 font-semibold">Ort. Süre/Görev</th>
                          <th className="text-center p-3 font-semibold">Yüksek</th>
                          <th className="text-center p-3 font-semibold">Orta</th>
                          <th className="text-center p-3 font-semibold">Düşük</th>
                        </tr>
                      </thead>
                      <tbody>
                        {performanceData.map((user, idx) => (
                          <tr key={idx} className="border-b hover:bg-gray-50">
                            <td className="p-3 font-medium">{user.user_name}</td>
                            <td className="text-center p-3">{user.assigned_tasks}</td>
                            <td className="text-center p-3">{user.completed_tasks}</td>
                            <td className="text-center p-3">
                              <span className={`inline-block px-2 py-1 rounded ${
                                user.completion_rate >= 80 ? 'bg-green-100 text-green-800' :
                                user.completion_rate >= 50 ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {user.completion_rate}%
                              </span>
                            </td>
                            <td className="text-center p-3">{formatTime(user.total_time_spent)}</td>
                            <td className="text-center p-3">{formatTime(user.avg_time_per_task)}</td>
                            <td className="text-center p-3">{user.high_priority}</td>
                            <td className="text-center p-3">{user.medium_priority}</td>
                            <td className="text-center p-3">{user.low_priority}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Performance Chart */}
            {performanceData.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Tamamlama Oranı Karşılaştırması</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="user_name" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="completion_rate" fill="#3B82F6" name="Tamamlanma Oranı (%)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Time Tab */}
          <TabsContent value="time" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* By User */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Kullanıcı Bazlı Zaman
                  </CardTitle>
                  <CardDescription>Toplam: {formatTime(timeData?.total_time || 0)}</CardDescription>
                </CardHeader>
                <CardContent>
                  {timeData?.by_user?.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Veri bulunamadı</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {timeData?.by_user?.map((user, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{user.user_name}</p>
                              <p className="text-sm text-gray-500">{user.task_count} görev</p>
                            </div>
                            <p className="text-lg font-bold text-blue-600">
                              {formatTime(user.total_time)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* By Project */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5" />
                    Proje Bazlı Zaman
                  </CardTitle>
                  <CardDescription>{timeData?.total_tasks || 0} görev</CardDescription>
                </CardHeader>
                <CardContent>
                  {timeData?.by_project?.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">Veri bulunamadı</p>
                  ) : (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {timeData?.by_project?.map((project, idx) => (
                        <div key={idx} className="p-3 border rounded-lg">
                          <div className="flex justify-between items-start mb-2">
                            <div>
                              <p className="font-semibold">{project.project_name}</p>
                              <p className="text-sm text-gray-500">{project.task_count} görev</p>
                            </div>
                            <p className="text-lg font-bold text-green-600">
                              {formatTime(project.total_time)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Time Chart */}
            {timeData?.by_user && timeData.by_user.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>Zaman Dağılımı</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={timeData.by_user}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="user_name" />
                      <YAxis />
                      <Tooltip formatter={(value) => formatTime(value)} />
                      <Legend />
                      <Bar dataKey="total_time" fill="#10B981" name="Toplam Süre (dk)" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Cost Burn Tab */}
          <TabsContent value="costburn" className="space-y-6">
            {selectedProject && selectedProject !== "all" ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="h-5 w-5" />
                      Proje: {projects.find(p => p.id === selectedProject)?.name || 'Seçilen Proje'}
                    </CardTitle>
                    <CardDescription>
                      Üstteki dropdown'dan farklı bir proje seçebilirsiniz
                    </CardDescription>
                  </CardHeader>
                </Card>
                <CostBurnReport projectId={selectedProject} />
              </div>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <DollarSign className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Maliyet Raporu
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Maliyet raporunu görmek için üstteki dropdown'dan bir proje seçin
                    </p>
                    <div className="text-sm text-gray-500">
                      💡 Proje dropdown'u sayfanın üst sağ köşesinde
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Utilization Tab */}
          <TabsContent value="utilization" className="space-y-6">
            {selectedProject && selectedProject !== "all" ? (
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Proje: {projects.find(p => p.id === selectedProject)?.name || 'Seçilen Proje'}
                    </CardTitle>
                    <CardDescription>
                      Kullanım raporunu görmek için aşağıdan bir insan kaynağı seçin
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResourceSelector projectId={selectedProject} />
                  </CardContent>
                </Card>
              </div>
            ) : (
              <Card>
                <CardContent className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Kullanım Raporu
                    </h3>
                    <p className="text-gray-600 mb-4">
                      Kullanım raporunu görmek için üstteki dropdown'dan bir proje seçin
                    </p>
                    <div className="text-sm text-gray-500">
                      💡 Proje dropdown'u sayfanın üst sağ köşesinde
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Export Tab */}
          <TabsContent value="export" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Download className="h-5 w-5" />
                  {t('exportExcel')}
                </CardTitle>
                <CardDescription>
                  {t('reportsDashboard')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={() => handleExport("tasks")}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('tasks')}</p>
                      <p className="text-xs text-gray-500">{t('exportTasks')}</p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={() => handleExport("projects")}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('projects')}</p>
                      <p className="text-xs text-gray-500">{t('exportProjects')}</p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={() => handleExport("members")}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('members')}</p>
                      <p className="text-xs text-gray-500">{t('exportMembers')}</p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={() => handleExport("all")}
                  >
                    <Download className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('all')}</p>
                      <p className="text-xs text-gray-500">{t('exportAllData')}</p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={handleExportCost}
                    disabled={!selectedProject || selectedProject === "all"}
                  >
                    <DollarSign className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('exportCostBurn')}</p>
                      <p className="text-xs text-gray-500">{t('costBurn')}</p>
                    </div>
                  </Button>

                  <Button
                    variant="outline"
                    className="h-24 flex flex-col gap-2"
                    onClick={handleExportAllocations}
                    disabled={!selectedProject || selectedProject === "all"}
                  >
                    <Activity className="h-6 w-6" />
                    <div className="text-center">
                      <p className="font-semibold">{t('exportAllocations')}</p>
                      <p className="text-xs text-gray-500">{t('utilization')}</p>
                    </div>
                  </Button>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-800">
                    💡 <strong>İpucu:</strong> Export edilen Excel dosyası birden fazla sayfa içerebilir. 
                    Görevler, Projeler ve Üyeler ayrı sayfalarda yer alır.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

export default Reports;
